import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { LanguageService } from '../../application/services/language.service';
import { SupportSessionInfo, SupportSessionService } from '../../application/services/support-session.service';

/**
 * Shown on every page of a support-session tab: company, read-only notice, time left and End.
 * When the time is up (or End is clicked) the tab's session is cleared.
 */
@Component({
  selector: 'app-support-session-banner',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div *ngIf="session" role="status"
      class="fixed bottom-4 z-[10000] flex flex-wrap items-center gap-3 rounded-lg px-4 py-2 text-sm shadow-lg"
      style="inset-inline-start: 50%; background: #92400e; color: #fff; max-width: calc(100vw - 2rem);"
      [style.transform]="isAr ? 'translateX(50%)' : 'translateX(-50%)'">
      <i class="fa fa-life-ring"></i>
      <span>
        <strong>{{ isAr ? 'جلسة دعم' : 'Support session' }}</strong> · {{ session.companyName }} ·
        {{ isAr ? 'عرض فقط' : 'read-only' }} ·
        {{ isAr ? 'متبقي' : 'ends in' }} <span dir="ltr">{{ remaining }}</span>
      </span>
      <button type="button" (click)="endSession()" [disabled]="ending"
        class="rounded bg-white/20 px-3 py-1 font-medium hover:bg-white/30">
        {{ isAr ? 'إنهاء الجلسة' : 'End session' }}
      </button>
    </div>
  `,
})
export class SupportSessionBannerComponent implements OnInit, OnDestroy {
  session: SupportSessionInfo | null = null;
  remaining = '';
  ending = false;
  isAr = false;
  private timer?: ReturnType<typeof setInterval>;
  private nav?: Subscription;

  constructor(
    private support: SupportSessionService,
    private router: Router,
    private lang: LanguageService,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    this.refresh();
    // The session appears after the landing page stores it.
    this.nav = this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => this.refresh());
  }

  ngOnDestroy(): void {
    if (this.timer) clearInterval(this.timer);
    this.nav?.unsubscribe();
  }

  private refresh(): void {
    this.isAr = (this.lang.getCurrent() || 'en') === 'ar';
    this.session = this.support.current();
    if (this.session && !this.timer) {
      this.tick();
      this.timer = setInterval(() => this.zone.run(() => this.tick()), 1000);
    } else if (!this.session && this.timer) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
  }

  private tick(): void {
    if (!this.session) return;
    const ms = new Date(this.session.expiresAt).getTime() - Date.now();
    if (ms <= 0) {
      this.finish();
      return;
    }
    const total = Math.floor(ms / 1000);
    this.remaining = `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
  }

  endSession(): void {
    this.ending = true;
    this.support.end().subscribe(() => this.finish());
  }

  private finish(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
    this.session = null;
    this.ending = false;
    this.support.clear();
    this.router.navigate(['/support-session'], { queryParams: { ended: 1 } });
  }
}
